import { useState, useEffect, Fragment } from "react";
import {
  Table,
  Container,
  Button,
  Row,
  Col,
  Form,
  Card,
  Badge,
  ButtonGroup,
  Modal,
} from "react-bootstrap";
import { useAuth } from "../Context/AuthContext";
import { toast } from "react-toastify";
import Sample14 from "../Components/Logo";
import "../Styling/vendormaster.css";
import { getVendorsByOrganisation } from "../Api/CustomerMaster";
import { useNavigate } from "react-router-dom";
import {
  createCusVenCp,
  fetchCusVenCpByCusVenType,
} from "../Api/CusVenCpMasterApi";

const VendorList = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [filteredVendors, setFilteredVendors] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [vendorTypeFilter, setVendorTypeFilter] = useState("all");

  const userDetails = JSON.parse(localStorage.getItem("userDetails")) || {};
  const organisationId = userDetails?.organisationId || user?.organisationId;

  const [expandedVendorCode, setExpandedVendorCode] = useState(null);
  const [vendorContactMap, setVendorContactMap] = useState({});
  const [loadingContacts, setLoadingContacts] = useState(false);

  const [showCpModal, setShowCpModal] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);

  const [cpForm, setCpForm] = useState({
    contactPerName: "",
    contactPerNumber: "",
    contactPerEmail: "",
  });

  const openCpModal = (vendor) => {
    setSelectedVendor(vendor);
    setCpForm({
      contactPerName: "",
      contactPerNumber: "",
      contactPerEmail: "",
    });
    setShowCpModal(true);
  };

  const closeCpModal = () => {
    setShowCpModal(false);
    setSelectedVendor(null);
  };

  const handleCpChange = (e) => {
    setCpForm({ ...cpForm, [e.target.name]: e.target.value });
  };

  const handleSaveContactPerson = async () => {
    try {
      if (!selectedVendor) return;

      const payload = {
        cusVenTypeId: selectedVendor.vendorCode, // ✅ vendorCode stored here
        contactPerName: cpForm.contactPerName,
        contactPerNumber: cpForm.contactPerNumber,
        contactPerEmail: cpForm.contactPerEmail,
        orgId: String(organisationId),
      };

      await createCusVenCp(payload, user.userId);

      toast.success("Contact person added successfully");
      closeCpModal();
    } catch (error) {
      console.error(error);
      toast.error("Failed to add contact person");
    }
  };

  // Fetch vendors when component mounts
  useEffect(() => {
    fetchVendors();
  }, [organisationId]);

  // Filter vendors based on search and type
  useEffect(() => {
    let filtered = vendors;

    if (searchTerm) {
      filtered = filtered.filter(
        (vendor) =>
          vendor.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          vendor.shortNames?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          vendor.vendorCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          vendor.taxId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          vendor.pan?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          vendor.address1?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (vendorTypeFilter !== "all") {
      filtered = filtered.filter((vendor) => vendor.type === vendorTypeFilter);
    }

    setFilteredVendors(filtered);
  }, [vendors, searchTerm, vendorTypeFilter]);

  const fetchVendors = async () => {
    setLoading(true);
    try {
      const orgIdToUse = organisationId ? parseInt(organisationId) : 2001;
      const vendorsData = await getVendorsByOrganisation(orgIdToUse);
      setVendors(vendorsData || []);
    } catch (error) {
      toast.error(`Error fetching vendors: ${error?.message || error}`);
      console.error("Vendor fetch error:", error);
      setVendors([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchVendors();
  };

  // Edit vendor function
  const handleEditVendor = (vendorCode) => {
    navigate(`/vendors/edit/${vendorCode}`);
  };

  // Add new vendor function
  const handleAddVendor = () => {
    navigate("/vendor-master");
  };

  const getStatusBadge = (active) => {
    return active === "Yes" || active === "Active" ? (
      <Badge bg="success">Active</Badge>
    ) : (
      <Badge bg="danger">Inactive</Badge>
    );
  };

  const getVendorTypeBadge = (type) => {
    const typeConfig = {
      manufacturer: { label: "Manufacturer", variant: "primary" },
      supplier: { label: "Supplier", variant: "info" },
    };

    const config = typeConfig[type] || { label: type, variant: "secondary" };
    return <Badge bg={config.variant}>{config.label}</Badge>;
  };

  const toggleVendorContacts = async (vendorCode) => {
    if (expandedVendorCode === vendorCode) {
      setExpandedVendorCode(null); // collapse
      return;
    }

    setExpandedVendorCode(vendorCode);

    // already loaded → do not refetch
    if (vendorContactMap[vendorCode]) return;

    try {
      setLoadingContacts(true);
      const data = await fetchCusVenCpByCusVenType(vendorCode);

      setVendorContactMap((prev) => ({
        ...prev,
        [vendorCode]: data || [],
      }));
    } catch (error) {
      toast.error("Failed to load vendor contact persons");
    } finally {
      setLoadingContacts(false);
    }
  };

  const formatAddress = (vendor) => {
    const addressParts = [
      vendor.address1,
      vendor.address2,
      vendor.address3,
      vendor.cityName,
      vendor.pincode,
    ].filter((part) => part && part.trim() !== "");

    return addressParts.length > 0
      ? addressParts.join(", ")
      : "No address provided";
  };
  console.log(filteredVendors);
  if (loading) {
    return (
      <div className="main-content expand">
        <Container fluid className="fields-container">
          <div className="loading-overlay">
            <Sample14 />
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="main-content expand">
      <Container fluid className="fields-container">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2 className="fields-title">Vendor List</h2>
          <div>
            <Button
              variant="outline-primary"
              onClick={handleRefresh}
              className="me-2"
            >
              Refresh
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <Row className="mb-4">
          <Col md={3}>
            <Card className="summary-card">
              <Card.Body>
                <Card.Title>Total Vendors</Card.Title>
                <Card.Text className="h4">{vendors.length}</Card.Text>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="summary-card">
              <Card.Body>
                <Card.Title>Manufacturers</Card.Title>
                <Card.Text className="h4">
                  {vendors.filter((v) => v.type === "manufacturer").length}
                </Card.Text>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="summary-card">
              <Card.Body>
                <Card.Title>Suppliers</Card.Title>
                <Card.Text className="h4">
                  {vendors.filter((v) => v.type === "supplier").length}
                </Card.Text>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="summary-card">
              <Card.Body>
                <Card.Title>Active Vendors</Card.Title>
                <Card.Text className="h4">
                  {
                    vendors.filter(
                      (v) => v.active === "Yes" || v.active === "Active"
                    ).length
                  }
                </Card.Text>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Filters */}
        <Card className="mb-4">
          <Card.Body>
            <Row>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Search Vendors</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Search by name, vendor code, short name, GST, PAN, address..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Vendor Type</Form.Label>
                  <Form.Select
                    value={vendorTypeFilter}
                    onChange={(e) => setVendorTypeFilter(e.target.value)}
                  >
                    <option value="all">All Types</option>
                    <option value="manufacturer">Manufacturer</option>
                    <option value="supplier">Supplier</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3} className="d-flex align-items-end">
                <div>
                  Showing {filteredVendors.length} of {vendors.length} vendors
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Vendors Table */}
        <Card>
          <Card.Body>
            {filteredVendors.length === 0 ? (
              <div className="text-center py-4">
                <h5>No vendors found</h5>
                <p className="text-muted">
                  {vendors.length === 0
                    ? "No vendors have been created yet."
                    : "No vendors match your search criteria."}
                </p>
                <Button variant="primary" onClick={fetchVendors}>
                  Try Again
                </Button>
              </div>
            ) : (
              <div className="table-responsive">
                <Table striped bordered hover>
                  <thead className="table-dark">
                    <tr>
                      <th></th>
                      <th>#</th>
                      <th>Vendor Code</th>
                      <th>Vendor Name</th>
                      <th>Short Name</th>
                      <th>Type</th>
                      <th>GST No</th>
                      <th>PAN</th>
                      <th>Address</th>
                      <th>Country</th>
                      <th>Status</th>
                      <th>Currency</th>
                      <th>Mobile Number</th> {/* Updated header */}
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredVendors.map((vendor, index) => (
                      <Fragment key={vendor.vendorCode}>
                        {/* ===== MAIN VENDOR ROW ===== */}
                        <tr>
                          {/* Arrow */}
                          <td style={{ textAlign: "center", width: "40px" }}>
                            <Button
                              variant="link"
                              size="sm"
                              onClick={() =>
                                toggleVendorContacts(vendor.vendorCode)
                              }
                            >
                              {expandedVendorCode === vendor.vendorCode
                                ? "▼"
                                : "▶"}
                            </Button>
                          </td>

                          <td>{index + 1}</td>
                          <td>
                            <strong>{vendor.vendorCode}</strong>
                          </td>
                          <td>
                            <strong>{vendor.name}</strong>
                          </td>
                          <td>{vendor.shortNames || "-"}</td>
                          <td>{getVendorTypeBadge(vendor.type)}</td>
                          <td>{vendor.gstno || "-"}</td>
                          <td>{vendor.pan || "-"}</td>
                          <td className="small">{formatAddress(vendor)}</td>
                          <td>{vendor.countryCode || "-"}</td>
                          <td>{getStatusBadge(vendor.active)}</td>
                          <td>{vendor.currency || "-"}</td>
                          <td>{vendor.mobileNumber || "-"}</td>

                          <td>
                            {/* <ButtonGroup size="sm">
                              <Button
                                onClick={() =>
                                  handleEditVendor(vendor.vendorCode)
                                }
                              >
                                Edit
                              </Button>

                              <Button
                                variant="outline-success"
                                className="ms-2"
                                onClick={() => openCpModal(vendor)}
                              >
                                👤
                              </Button>
                            </ButtonGroup> */}
                            <ButtonGroup size="sm">
                              <Button
                                onClick={() =>
                                  handleEditVendor(vendor.vendorCode)
                                }
                              >
                                Edit
                              </Button>

                              {/* <Button
                                variant="outline-success"
                                className="ms-2"
                                onClick={() => openCpModal(vendor)}
                              >
                                👤
                              </Button> */}

                              <Button
                                variant="outline-info"
                                className="ms-2"
                                onClick={() =>
                                  navigate(`/viewVendor/${vendor.vendorCode}`)
                                }
                              >
                                View
                              </Button>
                            </ButtonGroup>
                          </td>
                        </tr>

                        {/* ===== EXPANDED CONTACT PERSON ROW ===== */}
                        {expandedVendorCode === vendor.vendorCode && (
                          <tr>
                            <td colSpan={14} style={{ background: "#f9f9f9" }}>
                              {loadingContacts ? (
                                <p className="m-2">
                                  Loading contact persons...
                                </p>
                              ) : (
                                <>
                                  {(vendorContactMap[vendor.vendorCode]
                                    ?.length ?? 0) > 0 ? (
                                    <Table size="sm" bordered className="mb-0">
                                      <thead>
                                        <tr>
                                          <th>#</th>
                                          <th>Name</th>
                                          <th>Number</th>
                                          <th>Email</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {vendorContactMap[
                                          vendor.vendorCode
                                        ].map((cp, i) => (
                                          <tr key={cp.cusVenId}>
                                            <td>{i + 1}</td>
                                            <td>{cp.contactPerName}</td>
                                            <td>{cp.contactPerNumber}</td>
                                            <td>{cp.contactPerEmail}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </Table>
                                  ) : (
                                    <p className="m-2 text-muted">
                                      No contact persons found.
                                    </p>
                                  )}
                                </>
                              )}
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
        </Card>
      </Container>
      <Modal show={showCpModal} onHide={closeCpModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add Contact Person</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Contact Person Name</Form.Label>
              <Form.Control
                type="text"
                name="contactPerName"
                value={cpForm.contactPerName}
                onChange={handleCpChange}
                placeholder="Enter name"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Contact Person Number</Form.Label>
              <Form.Control
                type="text"
                name="contactPerNumber"
                value={cpForm.contactPerNumber}
                onChange={handleCpChange}
                placeholder="Enter number"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Contact Person Email</Form.Label>
              <Form.Control
                type="email"
                name="contactPerEmail"
                value={cpForm.contactPerEmail}
                onChange={handleCpChange}
                placeholder="Enter email"
              />
            </Form.Group>
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={closeCpModal}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveContactPerson}>
            Save
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default VendorList;
